import React, { Component, ErrorInfo, ReactNode } from "react";
import { classifyError, type ErrorCategory } from "@/lib/errorClassifier";
import BlockFallback, { type BlockType } from "@/components/BlockFallback";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Section name for telemetry */
  section?: string;
  /** Block type for the fallback UI */
  blockType?: BlockType;
  /** Recipe/content ID for context */
  recipeId?: string;
  /** Whether to show retry button */
  showRetry?: boolean;
  /** Compact fallback style */
  compact?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  category: ErrorCategory | null;
  retryCount: number;
}

/**
 * Lightweight error boundary for individual sections (ingredients, preparation, chat, etc).
 * Isolates failures so the rest of the page keeps working.
 * - Classifies errors (network / timeout / parsing / access / missing / unexpected)
 * - Fires telemetry on error and on retry
 * - Shows human-readable BlockFallback by default
 */
class SectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, category: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const { category } = classifyError(error);
    return { hasError: true, error, category };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { category } = classifyError(error);
    const section = this.props.section || "unknown";

    console.warn(`[SectionErrorBoundary:${section}] ${category}:`, error.message);
  }

  handleRetry = () => {
    const newCount = this.state.retryCount + 1;
    this.setState({ hasError: false, error: null, category: null, retryCount: newCount });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <BlockFallback
        blockType={this.props.blockType || "section"}
        category={this.state.category || "unexpected"}
        onRetry={this.props.showRetry !== false ? this.handleRetry : undefined}
        compact={this.props.compact}
        context={this.props.section}
      />
    );
  }
}

export default SectionErrorBoundary;
